<ng-template let-extraClass="extraClass" #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="pageTitle"
      [canGoBack]="true" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">
    @if (errorMessage) {
      <tidy-error-message [text]="errorMessage" />
    }

    @if (!isDataLoaded) {
      <div style="width: 100%">
        <ion-skeleton-text animated="true" style="height: 60px; border-radius: 6px" />
      </div>
      <div style="width: 100%; padding-top: 1rem;">
        <ion-skeleton-text animated="true" style="height: 160px; border-radius: 6px" />
      </div>
    }

    @if (isDataLoaded && target) {
      <form [formGroup]="form">
        @if (isAddFlow) {
          <tidy-row>
            <tidy-radio-button
              [label]="'Which guidebook?'"
              formControlName="audience"
              [form]="form.controls.audience"
              [items]="audienceOptions" />
          </tidy-row>
        }

        @if (showAddPicker) {
          <tidy-row>
            <tidy-select
              [label]="'What would you like to add?'"
              formControlName="addTarget"
              [form]="form.controls.addTarget"
              [items]="addOptions"
              [icon]="'assets/svg/add-circle-outline.svg'" />
          </tidy-row>
        }

        @if (target.type === 'text') {
          <tidy-row>
            <tidy-textarea
              [label]="textLabel"
              formControlName="text"
              [form]="form.controls.text"
              [icon]="'assets/svg/create-outline.svg'" />
          </tidy-row>
        }

        @if (target.type === 'category') {
          <tidy-row>
            <tidy-text [helper]="('Shown under ' | translate) + categoryLabel + (' in the guidebook. Empty fields are left out.' | translate)" />
          </tidy-row>
          @if (isFixedCategory) {
            <tidy-row>
              <tidy-text [helper]="'Synced with Edit Property. Changes here update both.'" />
            </tidy-row>
          }
          <div formGroupName="fields">
            @for (field of visibleCategoryFields; track field.key) {
              <tidy-row>
                @if (field.inputType === 'textarea') {
                  <tidy-textarea
                    [label]="field.label"
                    [formControl]="fieldControls[field.key]"
                    [icon]="'assets/svg/create-outline.svg'" />
                } @else if (field.inputType === 'select') {
                  <tidy-select
                    [label]="field.label"
                    [form]="fieldControls[field.key]"
                    [formControl]="fieldControls[field.key]"
                    [items]="fieldOptions[field.key]"
                    [icon]="'assets/svg/list-outline.svg'" />
                } @else {
                  <tidy-input
                    [label]="field.label"
                    [formControl]="fieldControls[field.key]"
                    [icon]="'assets/svg/create-outline.svg'" />
                }
                @if (field.fromAddressModel && !isFixedCategory) {
                  <tidy-text [helper]="'Synced from property setup. Edits here update the property record.'" />
                }
              </tidy-row>
            }
          </div>

          @if (photoType) {
            <tidy-row class="extra-top-padding" [alignLastItemRight]="true">
              <tidy-text [header]="'Photos'" />
              <tidy-text
                [body]="'Add Photo'"
                (action)="addPhoto()"
                class="link" />
            </tidy-row>
            <photo-caption
              [photoUrls]="photoUrls"
              (deletePhoto)="confirmDeletePhoto($event)"
              [type]="photoType" />
          }

          @if (isAccessCategory && addressId) {
            <guidebook-door-codes
              [addressId]="addressId"
              (openStay)="openStay($event)"
              (generateCode)="openGenerateCode($event)" />
          }
        }

        @if (target.type === 'category' && !isFixedCategory) {
          @if (categoryFields.length) {
            <tidy-row class="extra-top-padding">
              <tidy-text [header]="'Additional rows'" />
            </tidy-row>
            <tidy-row>
              <tidy-text [helper]="'Your own rows for this section, shown after the fields above.'" />
            </tidy-row>
          }
          <div formArrayName="rows">
            @for (row of rows.controls; track $index) {
              @if (!row.controls.deleted.value) {
                <div [formGroupName]="$index" class="field-top-padding">
                  <tidy-card>
                    <tidy-row>
                      <tidy-input
                        [label]="'Title'"
                        formControlName="label"
                        [form]="row.controls.label"
                        [icon]="'assets/svg/pricetag-outline.svg'" />
                    </tidy-row>
                    <tidy-row>
                      <tidy-textarea
                        [label]="'Content'"
                        formControlName="value"
                        [form]="row.controls.value"
                        [icon]="'assets/svg/create-outline.svg'" />
                    </tidy-row>
                    <tidy-row>
                      <tidy-text
                        [actionBody]="'Remove row'"
                        (action)="removeRow($index)"
                        class="delete-automation-link" />
                    </tidy-row>
                  </tidy-card>
                </div>
              }
            }
          </div>
          <tidy-row>
            <tidy-button
              [text]="'Add Row'"
              [action]="boundAddRow"
              class="secondary" />
          </tidy-row>
        }

        @if (target.type === 'custom') {
          @if (audience === 'guest') {
            <tidy-row>
              <tidy-select
                [label]="'Where it appears'"
                formControlName="placement"
                [form]="form.controls.placement"
                [items]="placementOptions"
                [icon]="'assets/svg/list-outline.svg'" />
            </tidy-row>
          }
          <tidy-row>
            <tidy-input
              [label]="'Title'"
              formControlName="label"
              [form]="form.controls.label"
              [icon]="'assets/svg/pricetag-outline.svg'"
              [submitted]="submitted"
              [errorMessage]="'Please enter a title'" />
          </tidy-row>
          <tidy-row>
            <tidy-textarea
              [label]="'Content'"
              formControlName="value"
              [form]="form.controls.value"
              [icon]="'assets/svg/create-outline.svg'" />
          </tidy-row>
        }

        <tidy-row class="extra-top-padding">
          <tidy-button
            [text]="saveLabel"
            [action]="boundSave"
            [disabled]="isSaving"
            class="primary" />
        </tidy-row>
      </form>
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content>
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
